import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { PATHS } from '@/routes/paths';
import { toast } from 'react-hot-toast';
import { Loader } from '@/components/custom/loader';
import { ReportKpiSkeleton } from '@/pages/reports/report-kpi-skeleton';
import { EmptyState } from '@/components/custom/empty-state';
import useReportAbilities from '@/hooks/useReportAbilities';
import { ReportsKpiIcon } from './reports-kpi-icon';
import usePermission from '@/hooks/usePermission';
import { DashboardContent } from '@/layouts/dashboard';
import { ReportCard, ReportCardSubtitle, ReportCardTitle, ReportCardWrapper, ReportKpi, ReportKpiBody, ReportKpiHeader, ReportKpiTitle, ReportKpiValue, ReportKpiWrapper } from '@/components/custom/report';
import { sassTrue } from 'sass';

export function Reports(props) {
  const {
    // selectedTab,
    uri
  } = props;

  const navigate = useNavigate();
  const client = useClient();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [info, setInfo] = useState([]);
  const { reportAbilities, getReportAbilities } = useReportAbilities();
  const ssoApi = process.env.REACT_APP_SSO_API_URL;
  const appName = process.env.REACT_APP_APP_NAME;

  const getReports = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('/embed_reports');
    if (error) {
      toast.error('Erro ao buscar relatórios');
    } else {
      setReports(data);
    }
    setLoading(false);
  };

  const getInfo = async () => {
    setLoadingInfo(true);
    const { data, error } = await client.fetch('/reports/learning_systems/general');
    if (error) {
      toast.error('Erro ao obter dados');
    } else {
      setInfo(data);
    }
    setLoadingInfo(false);
  };

  useEffect(() => {
    getReports();
    getInfo();
    getReportAbilities();
  }, []);

  const ReportsCard = ({ route, title, description }) => {
    let reportCardClass;

    switch (route) {
      case PATHS.REPORTS.COURSE_PROGRESS:
        reportCardClass = 'monitor';
        break;
      case PATHS.REPORTS.ACCESS:
        reportCardClass = 'globe';
        break;
      case PATHS.REPORTS.REGISTRATION:
        reportCardClass = 'user';
        break;
      case PATHS.REPORTS.ACCESS_FUNNEL:
        reportCardClass = 'mouse';
        break;
      case PATHS.REPORTS.ESSAY:
        reportCardClass = 'pencil';
        break;
      case PATHS.REPORTS.PLATFORM_USAGE:
        reportCardClass = 'mouse';
        break;
      case PATHS.REPORTS.LIVE_TRANSMISSION:
        reportCardClass = 'clock';
        break;
      case PATHS.REPORTS.STUDY_HOURS:
        reportCardClass = 'clock';
        break;

      default:
        break;
    }

    return (
      <ReportCard
        variant={reportCardClass}
        onClick={() => navigate(route)}
      >
        <ReportCardTitle title={title}>{title}</ReportCardTitle>
        <ReportCardSubtitle>{description}</ReportCardSubtitle>
      </ReportCard>
    );
  };

  const frontReports = useMemo(() => {
    const frontReports = [];

    if (reportAbilities) {
      if (reportAbilities?.['adesao'] && usePermission('Visualizar Relatório de Adesão', 'Relatórios') && ssoApi) {
        frontReports.push({
          route: PATHS.REPORTS.ADHERENCE,
          title: 'Adesão dos estudantes',
          description: 'Acompanhamento de cadastros e acessos a aulas ao vivo'
        });
      }
      if (reportAbilities?.['cadastro-e-notas']) {
        frontReports.push({ route: PATHS.REPORTS.SEDUC_PI_GERAL, title: 'Simulados e notas', description: 'Acompanhamento de simulados e notas no último simulado' });
      }

      if (reportAbilities?.['aulas-ao-vivo']?.['media-de-satisfacao'] || (reportAbilities?.['aulas-ao-vivo']?.['ranking'] && usePermission('Visualizar Relatório de Aulas ao Vivo', 'Relatórios'))) {
        frontReports.push({ route: PATHS.REPORTS.LIVE_CLASSES, title: 'Aulas ao vivo', description: 'Relatórios de satisfação do conteúdo das aulas' });
      }

      if (
        (reportAbilities?.['visao-global-dos-cadastros-de-usuarios']?.['mapa-de-usuarios-cadastrados'] || reportAbilities?.['visao-global-dos-cadastros-de-usuarios']?.['ranking']) &&
        usePermission('Visualizar Relatório de Cadastro', 'Relatórios')
      ) {
        frontReports.push({
          route: PATHS.REPORTS.REGISTRATION,
          title: 'Cadastros e Ativos',
          description: 'Visão global dos cadastros de usuários'
        });
      }
      if ((reportAbilities?.['mapeamento-de-acesso-do-usuario']?.['horarios-mais-acessados'] || reportAbilities?.['mapeamento-de-acesso-do-usuario']?.['mapa-de-calor']) && usePermission('Visualizar Relatório de Mapeamento de Acesso', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.ACCESS,
          title: 'Mapeamento de Acesso',
          description: 'Região geográfica e horários de maior atividade'
        });
      }

      if (reportAbilities?.['progresso-de-curso'] && usePermission('Visualizar Progresso dos Cursos', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.COURSE_PROGRESS,
          title: 'Progresso  dos Cursos',
          description: 'Acompanhe o andamento dos estudantes nos cursos.'
        });
      }

      if (reportAbilities?.['perguntas'] && usePermission('Visualizar Relatório de perguntas', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.QUESTIONS,
          title: 'Relatório de perguntas',
          description: 'Avalie a dificuldade de questões e pontos de atenção durante explicação'
        });
      }

      if (reportAbilities?.['respostas-avaliacoes'] && usePermission('Visualizar Relatório de respostas de avaliações', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.QUIZ,
          title: 'Relatório de respostas de avaliações',
          description: 'Objetivo: Identificar quais alunos, escolas e GRE responderam às avaliações '
        });
      }
      if (reportAbilities?.['acessos-aulas-ao-vivo'] && usePermission('Visualizar Relatório de acessos às aulas ao vivo', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.LIVE_ACCESS,
          title: 'Relatório de acessos às aulas ao vivo',
          description: 'Acompanhe o acesso de escolas e GREs'
        });
      }

      if (reportAbilities?.funil && usePermission('Visualizar Relatório de Funil de Acessos', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.ACCESS_FUNNEL,
          title: 'Funil de Acessos',
          description: 'Mostra a movimentação dos alunos em cursos e módulos.'
        });
      }

      if (reportAbilities?.['aulas-gravadas'] && usePermission('Visualizar Relatório de acessos às aulas gravadas', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.RECORD_CLASSES,
          title: 'Relatório de acessos às aulas gravadas',
          description: 'Acompanhe quais alunos, perfis, escolas ou GRE estão assistindo as aulas ao gravadas.'
        });
      }

      if (reportAbilities?.redacao && usePermission('Visualizar Redações', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.ESSAY,
          title: 'Redações',
          description: 'Detalhamento do envio e pontuação dos estudantes'
        });
      }
      if (reportAbilities?.['uso-da-plataforma'] && usePermission('Visualizar Relatório de Uso', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.PLATFORM_USAGE,
          title: 'Uso da Plataforma',
          description: 'Veja quais funcionalidades os alunos mais utilizam e como interagem com a plataforma ao longo do tempo.'
        });
      }
      if (reportAbilities?.['uptime'] && usePermission('Visualizar Relatório de Disponibilidade', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.UPTIME_MONTHLY,
          title: 'Disponibilidade da Plataforma',
          description: 'Monitora e apresenta o nível de disponibilidade do sistema ao longo do período.'
        });
      }
      if (reportAbilities?.['necessidade-intervencao'] && usePermission('Visualizar Relatório de Necessidade de Intervenção', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.INTERVENTION_NECESSITY,
          title: 'Necessidade de Intervenção',
          description: 'Classifica cursos por nível de risco (baixo, médio e alto), com base no desempenho na plataforma.'
        });
      }
      if (reportAbilities?.['transmissao-ao-vivo'] && usePermission('Visualizar Relatório de Transmissão ao Vivo', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.LIVE_TRANSMISSION,
          title: 'Transmissão ao vivo',
          description: 'Exibe transmissões de aulas ao vivo com horários de início e fim, totais por tema e lista detalhada por aula.'
        });
      }
      if (reportAbilities?.['performance_360'] && usePermission('Visualizar Relatório Performance 360', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.PERFORMANCE,
          title: 'Performance 360',
          description: 'Acompanhe o desempenho dos estudantes por regional, escola e aluno nas principais áreas de estudo.'
        });
      }
      if (reportAbilities?.['horas-estudo'] && usePermission('Visualizar Relatório de Horas de Estudo', 'Relatórios')) {
        frontReports.push({
          route: PATHS.REPORTS.STUDY_HOURS,
          title: 'Horas de Estudo',
          description: 'Monitora o consumo de conteúdo em vídeo pelos alunos'
        });
      }
    }

    return frontReports;
  }, [reportAbilities]);

  const breadcrumbs = {
    title: 'Análise',
    nav: [
      {
        route: uri,
        name: 'Relatórios',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <ReportKpiWrapper>
        {loadingInfo && [...Array(4)].map((_, index) => <ReportKpiSkeleton key={index} />)}

        {!loadingInfo &&
          info?.items?.map((item, index) => (
            <ReportKpi key={item?.label}>
              <ReportKpiHeader>
                <ReportsKpiIcon index={index} />
                <ReportKpiTitle>{item?.label || ''}</ReportKpiTitle>
              </ReportKpiHeader>
              <ReportKpiBody>
                <ReportKpiValue>{item?.value || ''}</ReportKpiValue>
              </ReportKpiBody>
            </ReportKpi>
          ))}
      </ReportKpiWrapper>

      <p className="mb-4">Acompanhe os índices de desempenho dos estudantes através de painéis de controle com indicadores individuais e coletivos. Selecione um painel para acessar as métricas</p>

      {reports?.length === 0 && loading && <Loader />}

      {frontReports.length === 0 && !loading && (
        <EmptyState
          type="data"
          text="Nenhum relatório disponível"
        />
      )}

      <ReportCardWrapper>
        {!loading &&
          frontReports?.map(item => (
            <ReportsCard
              route={item.route}
              title={item.title}
              description={item.description}
              key={item.route}
            />
          ))}
      </ReportCardWrapper>
    </DashboardContent>
  );
}
